import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useBookingContext } from "../context/BookingContext";
import moment from "moment";
import { capitalFirstLetter, findTypeLabel } from "../utils";
import PriceEstimate from "./PriceEstimate";
import InAndOutTownUsage from "./InAndOutTownUsage";
import { useTranslation } from "react-i18next";
import OutIslandUsage from "./OutIslandUsage";
import AdditionalInformationForm from "./AdditionalInformationForm";
import AdditionalChargeByDate from "./AdditionalChargeByDate";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import PlaceholderImg from "../images/components/placeholder.jpg";
import PrivacyPolicyCheckbox from "./PrivacyPolicyCheckbox";
import { getPolicyHtml } from "../utils/policies";
import { Notify } from "notiflix";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const MAX_FILE_SIZE = 1 * 1024 * 1024;
const SUPPORTED_FORMATS = ["image/jpeg", "image/png", "image/jpg"];

function CustomerForm({ onSubmit, disabled, priceCalculate }) {
  const { modal, setModal, data } = useBookingContext();
  const [identity, setIdentity] = React.useState("");
  const [identityImage, setIdentityImage] = React.useState(null);
  const [drivingLicense, setDrivingLicense] = React.useState(null);
  const [hotel, setHotel] = React.useState(null);
  const [flight, setFlight] = React.useState(null);
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const handleIdentityImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      Notify.failure("Unsupported file format. Please upload JPG or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      Notify.failure("File size too large. Maximum allowed size is 2MB.");
      event.target.value = "";
      return;
    }

    setIdentityImage(event.target.files);
    formik.setFieldValue("identity_image", file);
  };

  const handleDrivingLisence = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      Notify.failure("Unsupported file format. Please upload JPG or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      Notify.failure("File size too large. Maximum allowed size is 2MB.");
      event.target.value = "";
      return;
    }

    setDrivingLicense(event.target.files);
    formik.setFieldValue("driving_license", file);
  };

  const handleHotel = (event) => {
    setHotel(event.target.files);
  };
  const handleFlight = (event) => {
    setFlight(event.target.files);
  };

  // open modal when all inputs are fulfilled
  const closeModal = (e) => {
    e.preventDefault();
    setModal(false);
  };

  //formik
  const formik = useFormik({
    initialValues: {
      identity: "",
      driving_license: "",
      identity_image: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      additional_informations: {
        emergencyContact1: "",
        emergencyContact2: "",
        flightReservation: [],
        hotelReservation: [],
      },
      additional_items: [],
      notes: "",
      privacyPolicy: false,
    },
    validationSchema: Yup.object().shape({
      identity:
        data?.type === "self_drive" ? Yup.string().required("Identity type is required") : Yup.string().notRequired(),
      identity_image:
        data?.type === "self_drive" ? Yup.mixed().required("Identity image is required") : Yup.mixed().notRequired(),

      driving_license: Yup.mixed().test("custom-validation", "Driving license is required", function (value) {
        if (data?.type === "self_drive") {
          return value;
        } else {
          return true;
        }
      }),

      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Enter valid email")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter valid email")
        .required("Email is required"),
      phone: Yup.string().required("Whatsapp number is required").matches(phoneRegExp, "Enter valid Whatsapp number"),
      address: Yup.string().required("Address is required"),
      privacyPolicy: Yup.boolean().oneOf([true], "You must accept the privacy policy"),
      additional_informations:
        data?.type === "self_drive"
          ? Yup.object({
              emergencyContact1: Yup.number().required("Emergency contact 1 is required"),
              emergencyContact2: Yup.number().required("Emergency contact 2 is required"),
            })
          : Yup.object().notRequired(),
    }),

    onSubmit: (v) => {
      const cs = {
        ...v,
        identity_image: identityImage,
        driving_license: drivingLicense,
        hotel: hotel,
        flight: flight,
      };
      onSubmit(cs);
    },
  });

  // disable page scroll when modal is displayed
  useEffect(() => {
    if (modal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [modal]);

  useEffect(() => {
    if (!modal) {
      formik.resetForm({
        values: {
          ...formik.initialValues,
          additional_items: formik.values?.additional_items || [],
        },
      });
      setIdentity("");
      setIdentityImage(null);
      setDrivingLicense(null);
      setHotel(null);
      setFlight(null);
    }
  }, [modal]);

  const handleSelectIdentity = (e) => {
    const selected = e.target.value;
    setIdentity(selected);
    formik.setFieldValue("identity", selected);
  };

  const startDate = moment(data?.pickup_date, "YYYY-MM-DD");
  const endDate =
    data?.type !== "transfer_service"
      ? moment(data?.dropoff_date, "YYYY-MM-DD")
      : moment(data?.pickup_date).add(1, "days").format("YYYY-MM-DD");

  const datesInRange = [];
  let currentDate = startDate.clone();

  while (currentDate.isBefore(endDate)) {
    datesInRange.push(currentDate.format("YYYY-MM-DD"));
    currentDate.add(1, "days");
  }

  React.useEffect(() => {
    const additionalDatas = datesInRange?.map((val) => {
      return {
        date: val,
        umbrella: 0,
        baby_seat: 0,
        insurance: false,
        all_in_one_additional_items: false,
        wedding: false,
        additional_stop: false,
        with_driver: data?.type === "self_drive" ? data?.conditions?.with_driver : false,
      };
    });
    if (!formik.values?.additional_items?.length) {
      formik.setFieldValue("additional_items", additionalDatas);
    }
  }, []);

  return (
    <>
      <div className={`booking-modal ${modal ? "active-modal" : ""}`}>
        {/* title */}
        <div className="booking-modal__title">
          <h2>{t("complete_reservation")}</h2>
          <i onClick={closeModal} className="fa-solid fa-xmark"></i>
        </div>
        {/* message */}
        {/* <div className="booking-modal__message">
          <h4>
            <i className="fa-solid fa-circle-info"></i> {t("upon_completing")}
          </h4>
          <p>{t("your_rent_voucher")}</p>
        </div> */}
        {/* car info */}
        <div className="booking-modal__car-info">
          <div className="dates-div">
            <div className="booking-modal__car-info__dates">
              <h5>Booking</h5>
              <span>
                <i className="fa-solid fa-car"></i>
                <div>
                  <h6>{data?.conditions?.with_driver ? t("with_driver") : t("without_driver")}</h6>
                  <p>{findTypeLabel(data?.type)}</p>
                </div>
              </span>
              <h5>{t("location_n_date")}</h5>
              <span>
                <i className="fa-solid fa-location-dot"></i>
                <div>
                  <h6>{t("pickup_date_n_time")}</h6>
                  <p>{moment(data?.pickup_date).format("DD/MM/YYYY [-] HH:mm")}</p>
                </div>
              </span>
            </div>

            {data?.type !== "transfer_service" && (
              <div className="booking-modal__car-info__dates">
                <span>
                  <i className="fa-solid fa-location-dot"></i>
                  <div>
                    <h6>{t("dropoff_date_n_time")}</h6>
                    <p> {moment(data?.dropoff_date).format("DD/MM/YYYY [-] HH:mm")}</p>
                  </div>
                </span>
              </div>
            )}

            <div className="booking-modal__car-info__dates">
              <span>
                <i className="fa-solid fa-calendar-days"></i>
                <div>
                  <h6>{t("pickup_location")}</h6>
                  <p>{data?.pickup_location}</p>
                </div>
              </span>
            </div>

            <div className="booking-modal__car-info__dates">
              <span>
                <i className="fa-solid fa-calendar-days"></i>
                <div>
                  <h6>{t("dropoff_location")}</h6>
                  <p>{data?.dropoff_location}</p>
                </div>
              </span>
            </div>
          </div>
          <div className="booking-modal__car-info__model">
            <h5>
              <span>
                {t("car")} - {capitalFirstLetter(data?.car?.brands)} {capitalFirstLetter(data?.car?.name)}
              </span>
            </h5>
            {data?.car?.image_urls[0] ? (
              <Carousel dynamicHeight>
                {data.car?.image_urls.map((item, idx) => {
                  return (
                    <div key={idx}>
                      <img src={item} alt="car images" />
                    </div>
                  );
                })}
              </Carousel>
            ) : (
              <img src={PlaceholderImg} width={"100%"} alt="" />
            )}
          </div>
        </div>
        {data?.type === "tour" ? <InAndOutTownUsage /> : null}
        {data?.type === "self_drive" ? <OutIslandUsage /> : null}

        <div className="aditional_and_prices">
          <h5>{t("prices")}</h5>
          {/* <AditionalCharge formik={formik} type={data?.type} /> */}
          <AdditionalChargeByDate formik={formik} data={data} />
          <PriceEstimate priceCalculate={priceCalculate} type={data?.type} />
        </div>
        {/* personal info */}
        <div className="booking-modal__person-info">
          <h4>{t("personal_info")}</h4>

          <form onSubmit={formik.handleSubmit} className="info-form">
            {data?.type === "self_drive" ? (
              <div className="info-form__1col">
                <span>
                  <label>
                    {t("identity_type")} <b>*</b>
                  </label>
                  <select value={identity} defaultValue="" onChange={handleSelectIdentity}>
                    <option value="" disabled selected>
                      {t("select_identity")}
                    </option>
                    <option value="ktp">KTP</option>
                    <option value="passport">PASSPORT</option>
                    <option value="kitas">KITAS</option>
                  </select>
                  {formik.touched.identity && formik.errors.identity ? (
                    <p className="error-modal">{formik.errors.identity}</p>
                  ) : null}
                </span>
              </div>
            ) : null}
            <div
              className={`info-form${data?.type === "self_drive" ? "__2col" : "__1col"}`}
              style={{ padding: data?.type === "transfer_service" && 0 }}
            >
              {data?.type !== "transfer_service" ? (
                <span>
                  <label>
                    {t("identity_image")}
                    {data?.type === "self_drive" ? <b>*</b> : "(optional)"}
                  </label>
                  <input
                    key={modal ? "open" : "closed"}
                    type="file"
                    accept="image/jpeg, image/png, image/jpg"
                    onChange={handleIdentityImage}
                  ></input>
                  {formik.touched.identity_image && formik.errors.identity_image ? (
                    <p className="error-modal">{formik.errors.identity_image}</p>
                  ) : null}
                </span>
              ) : null}
              {data?.type === "self_drive" && (
                <span>
                  <label>
                    {t("sim_or")} <b>*</b>
                  </label>
                  <input
                    key={modal ? "open" : "closed"}
                    type="file"
                    accept="image/jpeg, image/png, image/jpg"
                    onChange={handleDrivingLisence}
                  ></input>
                  {formik.touched.driving_license && formik.errors.driving_license ? (
                    <p className="error-modal">{formik.errors.driving_license}</p>
                  ) : null}
                </span>
              )}
            </div>

            <div className="info-form__2col">
              <span>
                <label>
                  {t("booker_name")}
                  <b>*</b>
                </label>
                <input type="text" placeholder={t("enter_booker_name")} {...formik.getFieldProps("name")}></input>
                {formik.touched.name && formik.errors.name ? <p className="error-modal">{formik.errors.name}</p> : null}
              </span>

              <span>
                <label>
                  {t("whatsapp_number")} <b>*</b>
                </label>
                <PhoneInput
                  specialLabel={""}
                  enableSearch
                  country={"id"}
                  onChange={(v) => {
                    formik.setFieldValue("phone", v);
                  }}
                  name="phone"
                  value={formik.values.phone}
                  onBlur={formik.handleBlur}
                  inputStyle={{
                    width: "100%",
                    backgroundColor: "#dbdbdb",
                    borderRadius: 0,
                    border: "none",
                    fontSize: "1.5rem",
                    padding: "14px 8px 14px 58px",
                  }}
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <p className="error-modal">{formik.errors.phone}</p>
                ) : null}
              </span>

              <span>
                <label>
                  {t("email")} <b>*</b>
                </label>
                <input
                  type="email"
                  placeholder={t("enter_email")}
                  {...formik.getFieldProps("email")}
                  value={formik.values.email}
                  onChange={(e) => formik.setFieldValue("email", e.target.value.toLowerCase())}
                />
                {formik.touched.email && formik.errors.email ? (
                  <p className="error-modal">{formik.errors.email}</p>
                ) : null}
              </span>

              <span>
                <label>
                  {t("address")} <b>*</b>
                </label>
                <input type="text" placeholder={t("enter_address")} {...formik.getFieldProps("address")}></input>
                {formik.touched.address && formik.errors.address ? (
                  <p className="error-modal">{formik.errors.address}</p>
                ) : null}
              </span>
              {data?.type === "self_drive" ? (
                <>
                  <span>
                    <label>
                      {t("emergency_contact_1")} <b>*</b>
                    </label>
                    <PhoneInput
                      specialLabel={""}
                      enableSearch
                      country={"id"}
                      onChange={(v) => formik.setFieldValue("additional_informations.emergencyContact1", v)}
                      name="additional_informations.emergencyContact1"
                      value={formik.values?.additional_informations?.emergencyContact1 || ""}
                      onBlur={formik.handleBlur}
                      inputStyle={{
                        width: "100%",
                        backgroundColor: "#dbdbdb",
                        borderRadius: 0,
                        border: "none",
                        fontSize: "1.5rem",
                        padding: "14px 8px 14px 58px",
                      }}
                    />
                    {formik.touched?.additional_informations?.emergencyContact1 &&
                    formik.errors?.additional_informations?.emergencyContact1 ? (
                      <p className="error-modal">{formik.errors.additional_informations.emergencyContact1}</p>
                    ) : null}
                  </span>

                  <span>
                    <label>
                      {t("emergency_contact_2")} <b>*</b>
                    </label>
                    <PhoneInput
                      specialLabel={""}
                      enableSearch
                      country={"id"}
                      onChange={(v) => formik.setFieldValue("additional_informations.emergencyContact2", v)}
                      name="additional_informations.emergencyContact2"
                      value={formik.values?.additional_informations?.emergencyContact2 || ""}
                      onBlur={formik.handleBlur}
                      inputStyle={{
                        width: "100%",
                        backgroundColor: "#dbdbdb",
                        borderRadius: 0,
                        border: "none",
                        fontSize: "1.5rem",
                        padding: "14px 8px 14px 58px",
                      }}
                    />
                    {formik.touched?.additional_informations?.emergencyContact2 &&
                    formik.errors?.additional_informations?.emergencyContact2 ? (
                      <p className="error-modal">{formik.errors.additional_informations.emergencyContact2}</p>
                    ) : null}
                  </span>
                </>
              ) : null}
              <AdditionalInformationForm
                key={modal ? "open" : "closed"}
                formik={formik}
                data={data}
                handleFlight={handleFlight}
                handleHotel={handleHotel}
              />
            </div>
            <div className="info-form__1col">
              <span>
                <label>{t("notes")}</label>
                <textarea placeholder={t("enter_notes")} rows={3} {...formik.getFieldProps("notes")} />
              </span>
            </div>

            <span className="info-form__checkbox">
              <i className="fa-solid fa-circle-exclamation"></i> <p>{t("we_will_ask")}</p>
            </span>
            <PrivacyPolicyCheckbox
              checked={formik.values.privacyPolicy}
              onChange={(val) => formik.setFieldValue("privacyPolicy", val)}
              error={formik.touched.privacyPolicy && Boolean(formik.errors.privacyPolicy)}
              helperText={formik.errors.privacyPolicy}
              policyHtml={getPolicyHtml(data?.type, currentLanguage)}
            />

            <div className={`reserve-button${disabled ? "__disabled" : ""}`}>
              <button disabled={disabled} type="submit">
                {disabled ? t("loading") : t("reserve_now")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default CustomerForm;
